/**
 * Query param (`1` or `true`) that suppresses the redirect to `/welcome`.
 * `client/` honors it only on `/art*`; Town Square honors it on any path.
 */
export const DEFER_WELCOME_QUERY = 'defer_welcome';

/** Kill switch for the post-signup welcome wizard. OFF disables the redirect and the /welcome route. */
export const WELCOME_FLOW_FLAG = 'welcome_flow';

/** Account-management paths a user may reach mid-welcome-wizard (issue #3642). */
export const ONBOARDING_ALLOWED_PATHS = ['/settings', '/billing', '/plans'] as const;

/**
 * Whether a path is account management the wizard must not bounce away from.
 *
 * Exact match only, bar a trailing slash and case. Descendants are deliberately
 * excluded: none of the three has child routes, so `/settings/anything` — and,
 * since the client has no locale-prefixed routes, `/pt/settings` too — renders
 * `NotFound`. Exempting one would suppress the bounce on a page that is only a
 * 404, and `NotFound` renders outside `AppLayout`, so the resume banner could not
 * rescue the user either. Left unexempted, they are simply bounced to `/welcome`.
 *
 * These are `client/` routes. Town Square deliberately does not consult this
 * list: it has no account-management routes of its own, so `/settings` there is
 * an artist profile, and exempting it would only stop the lock from working for
 * an artist whose slug happens to be `settings`. A cross-app link to one of
 * these paths lands on the client origin, where this matcher runs.
 */
export function isOnboardingAllowedPath(pathname: string): boolean {
  if (!pathname) return false;
  // react-router compiles routes case-insensitively, so `/Settings` renders the
  // page; matching case-sensitively here would bounce a user off a page that did
  // render.
  // react-router appends `\/*$`, so `/settings//` renders the page too.
  const stripped = pathname.toLowerCase().replace(/\/+$/, '');
  return ONBOARDING_ALLOWED_PATHS.some((path) => stripped === path);
}
